// A labeled federal estimate built from the single-purpose calculations in calc.ts.
// It is an estimate, not a return: the scope is listed in `notIncluded` and shown to the user.
import { D, type Dec, ZERO, max0, minD, fmt } from "./money.ts";
import { RULES_2026 as R, type FilingStatus } from "./rules/2026.ts";
import {
  type TraceStep,
  split1256,
  seTax,
  additionalMedicareTax,
  niit,
  incomeTax,
  ordinaryTax,
} from "./calc.ts";

export interface EstimateInput {
  status: FilingStatus;
  /** Taxable W-2 wages (box 1) for everyone on the return. */
  wages: Dec;
  /**
   * Social Security wages and tips (W-2 boxes 3 and 7) of the person who has the prop profit. On a joint
   * return the spouse's wages never use up this person's Social Security wage base (Schedule SE is per person).
   */
  traderSocialSecurityWages: Dec;
  /** Medicare wages (W-2 box 5) for everyone on the return; Form 8959 combines both spouses. */
  medicareWages: Dec;
  /** Federal income tax withheld from wages. */
  withholding: Dec;
  /** Prop-firm payouts net of deductible expenses (Schedule C net profit). Can be negative. */
  propNetProfit: Dec;
  /** Net Section 1256 gain or (loss) for the year (Form 6781 line 7). */
  net1256: Dec;
  /** Taxable interest. Ordinary income that also counts as net investment income for NIIT. */
  interest: Dec;
  /** Other ordinary income with no SE tax that isn't investment income (unemployment, for example). */
  otherOrdinaryIncome: Dec;
  /**
   * Qualified business income deduction treatment. Whether prop payouts are a "specified service"
   * (trading) is unresolved, which only matters above the threshold. null = not answered yet.
   */
  qbi: { include: boolean; specifiedService: boolean | null };
}

export type EstimateResult =
  | { state: "needs_input"; required: string[]; trace: TraceStep[] }
  | { state: "invalid"; errors: string[]; trace: TraceStep[] }
  | {
      state: "computed";
      agi: Dec;
      taxableIncome: Dec;
      qbiDeduction: Dec;
      capitalLossDeduction: Dec;
      /** Total capital loss carried to 2027 (Schedule D Capital Loss Carryover Worksheet). */
      capitalLossCarryforward: Dec;
      shortTermCarryforward: Dec;
      longTermCarryforward: Dec;
      incomeTax: Dec;
      seTax: Dec;
      additionalMedicareTax: Dec;
      niit: Dec;
      totalTax: Dec;
      balanceAfterWithholding: Dec;
      /** Total tax ÷ (wages + prop profit + 1256 gain + other income), when that income is positive. */
      effectiveRate: Dec | null;
      /** Tax rate on the next dollar of ordinary income (rate schedule only). */
      marginalOrdinaryRate: Dec;
      trace: TraceStep[];
      notIncluded: string[];
    };

export const NOT_INCLUDED = [
  "Itemized deductions (the standard deduction is used)",
  "Credits (child, education, energy and others)",
  "Age 65+ and blindness deductions, including the 2025–2028 senior deduction",
  "Alternative minimum tax",
  "Other capital gains and losses, dividends and capital loss carryovers from earlier years",
  "Section 1256 loss carryback elections (Form 6781 box D)",
  "Retirement contributions and health insurance deductions",
  "State and local tax",
];

/** Amounts that can't be negative. Net profit and the 1256 result can be. */
const NON_NEGATIVE = ["wages", "traderSocialSecurityWages", "medicareWages", "withholding", "interest", "otherOrdinaryIncome"] as const;

export function estimateFederal(input: EstimateInput): EstimateResult {
  const trace: TraceStep[] = [];
  const status = input.status;
  const errors = NON_NEGATIVE.filter((k) => input[k].lessThan(0));
  if (errors.length) return { state: "invalid", errors: [...errors], trace };

  // Self-employment tax first: half of it reduces AGI.
  const se = seTax({ netProfit: input.propNetProfit, socialSecurityWages: input.traderSocialSecurityWages });
  trace.push(...se.trace);

  // Section 1256: 40% short-term, 60% long-term. A net loss is limited to $3,000 ($1,500 MFS) this year.
  const split = split1256(input.net1256);
  trace.push(...split.trace);
  let capitalIncome = input.net1256;
  let capitalLossDeduction = ZERO;
  if (input.net1256.lessThan(0)) {
    const limit = new D(status === "mfs" ? "1500" : "3000");
    capitalLossDeduction = minD(input.net1256.abs(), limit);
    capitalIncome = capitalLossDeduction.neg();
    trace.push({ label: "Net capital loss allowed this year", value: fmt(capitalLossDeduction), rule: "IRC §1211(b)" });
  }

  const agi = input.wages
    .plus(input.propNetProfit)
    .plus(capitalIncome)
    .plus(input.interest)
    .plus(input.otherOrdinaryIncome)
    .minus(se.value.deductibleHalf);
  trace.push({ label: "Adjusted gross income", value: fmt(agi) });

  const standard = new D(R.standardDeduction[status]);
  const beforeQbi = max0(agi.minus(standard));
  trace.push(
    { label: "Standard deduction", value: fmt(standard), rule: R.sources.standardDeduction },
    { label: "Taxable income before the QBI deduction", value: fmt(beforeQbi) },
  );

  // Net capital gain for rate purposes: the long-term share when the year nets to a gain.
  const netCapitalGain = input.net1256.greaterThan(0) ? split.value.longTerm : ZERO;

  let qbiDeduction = ZERO;
  if (input.qbi.include && input.propNetProfit.greaterThan(0)) {
    const q = qbi(status, input.propNetProfit.minus(se.value.deductibleHalf), beforeQbi, netCapitalGain, input.qbi.specifiedService);
    if (q.state === "needs_input") return { state: "needs_input", required: q.required, trace };
    qbiDeduction = q.value;
    trace.push(...q.trace);
  } else {
    trace.push({ label: "QBI deduction not included", value: "0.00" });
  }

  const taxableIncome = max0(beforeQbi.minus(qbiDeduction));
  trace.push({ label: "Taxable income", value: fmt(taxableIncome) });

  const carry = lossCarryover(agi.minus(standard).minus(qbiDeduction), capitalLossDeduction, split.value.shortTerm, split.value.longTerm);
  if (input.net1256.lessThan(0)) trace.push(...carry.trace);

  const it = incomeTax(status, taxableIncome, netCapitalGain);
  trace.push(...it.trace);
  const am = additionalMedicareTax(status, input.medicareWages, se.value.taxableSeIncome);
  trace.push(...am.trace);
  // Interest plus the futures result. Up to the allowed $3,000 capital loss can reduce other investment
  // income, but the total can't go below zero (Treas. Reg. §1.1411-4(d)). Prop payouts aren't investment income.
  const nii = max0(input.interest.plus(capitalIncome));
  trace.push({ label: "Net investment income (interest and futures)", value: fmt(nii), rule: R.sources.niit });
  const ni = niit(status, nii, agi);
  trace.push(...ni.trace);

  const totalTax = it.value.plus(se.value.seTax).plus(am.value).plus(ni.value);
  const balance = totalTax.minus(input.withholding);
  trace.push(
    { label: "Estimated total federal tax", value: fmt(totalTax) },
    { label: "Less withholding", value: fmt(input.withholding) },
    { label: balance.lessThan(0) ? "Estimated refund" : "Estimated balance due", value: fmt(balance.abs()) },
  );

  const grossIncome = input.wages.plus(input.propNetProfit).plus(input.net1256).plus(input.interest).plus(input.otherOrdinaryIncome);
  const effectiveRate = grossIncome.greaterThan(0) ? totalTax.div(grossIncome) : null;
  const ordinaryPart = taxableIncome.minus(minD(netCapitalGain, taxableIncome));
  const marginalOrdinaryRate = ordinaryTax(status, ordinaryPart.plus(1)).minus(ordinaryTax(status, ordinaryPart));

  return {
    state: "computed",
    agi,
    taxableIncome,
    qbiDeduction,
    capitalLossDeduction,
    capitalLossCarryforward: carry.value.shortTerm.plus(carry.value.longTerm),
    shortTermCarryforward: carry.value.shortTerm,
    longTermCarryforward: carry.value.longTerm,
    incomeTax: it.value,
    seTax: se.value.seTax,
    additionalMedicareTax: am.value,
    niit: ni.value,
    totalTax,
    balanceAfterWithholding: balance,
    effectiveRate,
    marginalOrdinaryRate,
    trace,
    notIncluded: NOT_INCLUDED,
  };
}

/**
 * Schedule D Capital Loss Carryover Worksheet for a year whose only capital item is the Section 1256 result.
 * A low-income year doesn't use up the loss: only the part of this year's loss deduction that taxable
 * income actually absorbed is subtracted. Short-term loss is used first.
 * `taxableIncome` is AGI less deductions and may be negative. Loss inputs are signed (negative = loss).
 */
export function lossCarryover(
  taxableIncome: Dec,
  lossDeduction: Dec,
  shortTerm: Dec,
  longTerm: Dec,
): { value: { shortTerm: Dec; longTerm: Dec }; trace: TraceStep[] } {
  if (lossDeduction.isZero()) return { value: { shortTerm: ZERO, longTerm: ZERO }, trace: [] };
  const line4 = minD(lossDeduction, max0(taxableIncome.plus(lossDeduction))); // loss deduction actually absorbed
  const stLoss = max0(shortTerm.neg());
  const ltLoss = max0(longTerm.neg());
  const stGain = max0(shortTerm);
  const ltGain = max0(longTerm);
  const stCarry = max0(stLoss.minus(line4.plus(ltGain)));
  const ltCarry = max0(ltLoss.minus(stGain.plus(max0(line4.minus(stLoss)))));
  return {
    value: { shortTerm: stCarry, longTerm: ltCarry },
    trace: [
      { label: "Taxable income before the loss limit is applied (can be below zero)", value: fmt(taxableIncome), rule: "Schedule D instructions, Capital Loss Carryover Worksheet" },
      { label: "Part of this year's loss deduction your income absorbed", value: fmt(line4) },
      { label: "Short-term loss carried to 2027", value: fmt(stCarry) },
      { label: "Long-term loss carried to 2027", value: fmt(ltCarry) },
    ],
  };
}

type QbiResult = { state: "computed"; value: Dec; trace: TraceStep[] } | { state: "needs_input"; required: string[] };

/**
 * §199A deduction for one business with no W-2 wages paid and no qualified property (the usual prop trader).
 * Above the threshold the deduction phases down; with no wages or property it reaches zero at the end
 * of the phase-in range whether or not the business is a specified service.
 */
export function qbi(
  status: FilingStatus,
  qualifiedBusinessIncome: Dec,
  taxableIncomeBeforeQbi: Dec,
  netCapitalGain: Dec,
  specifiedService: boolean | null,
): QbiResult {
  const threshold = new D(R.qbi.threshold[status]);
  const end = new D(R.qbi.phaseInEnd[status]);
  const qbiAmt = max0(qualifiedBusinessIncome);
  const full = qbiAmt.times("0.20");
  const trace: TraceStep[] = [{ label: "Qualified business income (profit less half of SE tax)", value: fmt(qbiAmt), rule: R.sources.qbi }];

  // Above the threshold the specified-service answer changes both the phase-down and whether the
  // $400 minimum applies, so it must be answered rather than assumed.
  if (taxableIncomeBeforeQbi.greaterThan(threshold) && specifiedService === null) {
    return { state: "needs_input", required: ["qbi.specifiedService"] };
  }

  let component: Dec;
  if (taxableIncomeBeforeQbi.lessThanOrEqualTo(threshold)) {
    component = full;
    trace.push({ label: "20% of QBI (taxable income at or under the threshold)", value: fmt(component) });
  } else if (taxableIncomeBeforeQbi.greaterThanOrEqualTo(end)) {
    component = ZERO;
    trace.push({ label: "Above the phase-in range with no W-2 wages or property, so no QBI deduction", value: "0.00" });
  } else {
    const ratio = taxableIncomeBeforeQbi.minus(threshold).div(end.minus(threshold));
    const keep = new D(1).minus(ratio);
    // Non-SSTB: the wage limit (zero) phases in, leaving 20% × QBI × (1 − ratio).
    // SSTB: QBI is first reduced to its applicable percentage (1 − ratio), then the same phase-in applies.
    component = specifiedService ? full.times(keep).times(keep) : full.times(keep);
    trace.push({
      label: specifiedService
        ? "Inside the phase-in range, treated as a specified service"
        : "Inside the phase-in range, not treated as a specified service",
      value: fmt(component),
    });
  }

  const incomeLimit = max0(taxableIncomeBeforeQbi.minus(netCapitalGain)).times("0.20");
  let value = minD(component, incomeLimit);
  trace.push({ label: "Limited to 20% of taxable income less net capital gain", value: fmt(value) });

  // OBBBA minimum deduction from 2026: $400 when active QBI is at least $1,000 (IRC §199A(i)).
  // A specified service above the phase-in range is not a qualified business, so the minimum can't apply.
  const stillQualified = !(specifiedService === true && taxableIncomeBeforeQbi.greaterThanOrEqualTo(end));
  if (stillQualified && qbiAmt.greaterThanOrEqualTo(R.qbi.minimumActiveQbi) && value.lessThan(R.qbi.minimumDeduction)) {
    value = new D(R.qbi.minimumDeduction);
    trace.push({ label: "Minimum $400 deduction (active QBI of $1,000 or more)", value: fmt(value), rule: "IRC §199A(i) (OBBBA §70105)" });
  }
  return { state: "computed", value, trace };
}
